import React, { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TableSortLabel,
  TextField,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { Voter } from "../types/index"

interface VoterTableProps {
  voters: Voter[]
  onEdit: (voter: Voter) => void
  onDelete: (voter: Voter) => void
}

const VoterTable: React.FC<VoterTableProps> = ({
  voters,
  onEdit,
  onDelete,
}) => {
  const [sortField, setSortField] = useState<keyof Voter | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [searchText, setSearchText] = useState("")

  const handleSort = (field: keyof Voter) => {
    const isAsc = sortField === field && sortDirection === "asc"
    setSortDirection(isAsc ? "desc" : "asc")
    setSortField(field)
  }

  const filteredVoters = React.useMemo(() => {
    return voters.filter(
      (voter) =>
        voter.username.toLowerCase().includes(searchText.toLowerCase()) ||
        voter.name.toLowerCase().includes(searchText.toLowerCase()) ||
        voter.email.toLowerCase().includes(searchText.toLowerCase())
    )
  }, [voters, searchText])

  const sortedAndFilteredVoters = React.useMemo(() => {
    if (!sortField) return filteredVoters

    return [...filteredVoters].sort((a, b) => {
      if (a[sortField] < b[sortField]) {
        return sortDirection === "asc" ? -1 : 1
      }
      if (a[sortField] > b[sortField]) {
        return sortDirection === "asc" ? 1 : -1
      }
      return 0
    })
  }, [filteredVoters, sortField, sortDirection])

  return (
    <>
      <TextField
        label="Search Voters"
        variant="outlined"
        fullWidth
        placeholder="Search by username, name, or email"
        margin="normal"
        onChange={(e) => setSearchText(e.target.value)}
      />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === "username"}
                  direction={sortField === "username" ? sortDirection : "asc"}
                  onClick={() => handleSort("username")}
                >
                  Username
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "name"}
                  direction={sortField === "name" ? sortDirection : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "email"}
                  direction={sortField === "email" ? sortDirection : "asc"}
                  onClick={() => handleSort("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>Operations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Add the total voter count row */}
            <TableRow>
              <TableCell colSpan={3} align="right">
                Total Voters:
              </TableCell>
              <TableCell>{sortedAndFilteredVoters.length}</TableCell>
            </TableRow>
            {sortedAndFilteredVoters.map((voter) => (
              <TableRow key={voter.id}>
                <TableCell>{voter.username}</TableCell>
                <TableCell>{voter.name}</TableCell>
                <TableCell>{voter.email}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onEdit(voter)}
                    style={{ marginRight: "8px" }}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => onDelete(voter)}
                  >
                    <DeleteIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default VoterTable
